import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Group as GroupIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  PersonAdd as PersonAddIcon,
  ExitToApp as LeaveIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../utils/authUtils';

function PrayerCircles() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [myCircles, setMyCircles] = useState([]);
  const [publicCircles, setPublicCircles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [newCircle, setNewCircle] = useState({
    name: '',
    description: '',
    is_private: false,
    requires_approval: false,
  });

  useEffect(() => {
    fetchMyCircles();
    fetchPublicCircles();
  }, []);

  const fetchMyCircles = async () => {
    try {
      const response = await api.get('/circles/my?include_pending=true');
      setMyCircles(response.data.circles || []);
    } catch (error) {
      console.error('Error fetching my circles:', error);
      showSnackbar('Failed to load your circles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicCircles = async () => {
    try {
      const response = await api.get('/circles/public?limit=20');
      setPublicCircles(response.data.circles || []);
    } catch (error) {
      console.error('Error fetching public circles:', error);
    }
  };

  const handleCreateCircle = async () => {
    if (!newCircle.name.trim()) {
      showSnackbar('Circle name is required', 'error');
      return;
    }

    try {
      const response = await api.post('/circles', newCircle);
      showSnackbar('Circle created successfully!', 'success');
      setCreateDialogOpen(false);
      setNewCircle({ name: '', description: '', is_private: false, requires_approval: false });
      fetchMyCircles();
    } catch (error) {
      console.error('Error creating circle:', error);
      showSnackbar('Failed to create circle', 'error');
    }
  };

  const handleJoinCircle = async (circleId) => {
    try {
      const response = await api.post(`/circles/${circleId}/join`);
      showSnackbar(response.data.message, 'success');
      fetchMyCircles();
      fetchPublicCircles();
    } catch (error) {
      console.error('Error joining circle:', error);
      showSnackbar(error.response?.data?.error || 'Failed to join circle', 'error');
    }
  };

  const handleLeaveCircle = async (circleId) => {
    if (!window.confirm('Are you sure you want to leave this circle?')) {
      return;
    }

    try {
      await api.post(`/circles/${circleId}/leave`);
      showSnackbar('Left circle successfully', 'success');
      fetchMyCircles();
    } catch (error) {
      console.error('Error leaving circle:', error);
      showSnackbar(error.response?.data?.error || 'Failed to leave circle', 'error');
    }
  };

  const handleDeleteCircle = async (circleId) => {
    if (!window.confirm('Are you sure you want to delete this circle? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/circles/${circleId}`);
      showSnackbar('Circle deleted successfully', 'success');
      fetchMyCircles();
    } catch (error) {
      console.error('Error deleting circle:', error);
      showSnackbar(error.response?.data?.error || 'Failed to delete circle', 'error');
    }
  };

  const handleSearchCircles = async () => {
    if (!searchQuery.trim()) {
      showSnackbar('Please enter a search query', 'error');
      return;
    }

    try {
      const response = await api.get(`/circles/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.circles || []);
      showSnackbar(`Found ${response.data.circles.length} circles`, 'info');
    } catch (error) {
      console.error('Error searching circles:', error);
      showSnackbar('Failed to search circles', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const renderCircleCard = (circle, showJoinButton = false, showLeaveButton = false) => (
    <Grid item xs={12} sm={6} md={4} key={circle.id}>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <GroupIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {circle.name}
            </Typography>
            {circle.is_private && <LockIcon fontSize="small" color="action" />}
            {!circle.is_private && <PublicIcon fontSize="small" color="action" />}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {circle.description || 'No description'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<GroupIcon />}
              label={`${circle.member_count || 0} members`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {circle.requires_approval && (
              <Chip label="Requires Approval" size="small" color="warning" variant="outlined" />
            )}
            {circle.membership_status === 'pending' && (
              <Chip label="Pending Approval" size="small" color="info" variant="outlined" />
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Created by: {circle.creator_username || 'Unknown'}
          </Typography>
        </CardContent>

        <CardActions>
          {showJoinButton && (
            <Button
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => handleJoinCircle(circle.id)}
            >
              Join
            </Button>
          )}
          {showLeaveButton && circle.membership_status === 'approved' && (
            <>
              <Button
                size="small"
                startIcon={<LeaveIcon />}
                onClick={() => handleLeaveCircle(circle.id)}
                color="warning"
              >
                Leave
              </Button>
              <Button
                size="small"
                onClick={() => navigate(`/circles/${circle.id}`)}
              >
                View Prayers
              </Button>
            </>
          )}
        </CardActions>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            Prayer Circles
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Circle
          </Button>
        </Box>

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="My Circles" />
          <Tab label="Discover" />
          <Tab label="Search" />
        </Tabs>

        {/* My Circles Tab */}
        {tabValue === 0 && (
          <Box>
            {myCircles.length === 0 ? (
              <Alert severity="info">
                You haven't joined any circles yet. Discover public circles or create your own!
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {myCircles.map(circle => renderCircleCard(circle, false, true))}
              </Grid>
            )}
          </Box>
        )}

        {/* Discover Tab */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Public Circles
            </Typography>
            {publicCircles.length === 0 ? (
              <Alert severity="info">No public circles available. Be the first to create one!</Alert>
            ) : (
              <Grid container spacing={3}>
                {publicCircles.map(circle => renderCircleCard(circle, true, false))}
              </Grid>
            )}
          </Box>
        )}

        {/* Search Tab */}
        {tabValue === 2 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Search circles by name or description"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchCircles()}
              />
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearchCircles}
              >
                Search
              </Button>
            </Box>

            {searchResults.length > 0 && (
              <Grid container spacing={3}>
                {searchResults.map(circle => renderCircleCard(circle, true, false))}
              </Grid>
            )}

            {searchQuery && searchResults.length === 0 && (
              <Alert severity="info">No circles found matching your search.</Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Create Circle Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Prayer Circle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Circle Name"
              fullWidth
              required
              value={newCircle.name}
              onChange={(e) => setNewCircle({ ...newCircle, name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newCircle.description}
              onChange={(e) => setNewCircle({ ...newCircle, description: e.target.value })}
            />
            <Box>
              <Button
                variant={newCircle.is_private ? 'contained' : 'outlined'}
                onClick={() => setNewCircle({ ...newCircle, is_private: !newCircle.is_private })}
                startIcon={newCircle.is_private ? <LockIcon /> : <PublicIcon />}
                sx={{ mr: 1 }}
              >
                {newCircle.is_private ? 'Private' : 'Public'}
              </Button>
              <Button
                variant={newCircle.requires_approval ? 'contained' : 'outlined'}
                onClick={() => setNewCircle({ ...newCircle, requires_approval: !newCircle.requires_approval })}
                startIcon={<CheckIcon />}
              >
                {newCircle.requires_approval ? 'Requires Approval' : 'Open Join'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateCircle} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default PrayerCircles;
